import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams, HttpContext } from '@angular/common/http';
import { Observable, tap, map } from 'rxjs';
import { SUPPRESS_ERROR_TOAST } from '../../interceptors/api.context';
/**
 * Import types from the model file
 */
import { Order, OrderApiResponse, OrderStatistics, OrderFilters, CustomerOrderPayload, OrderDetailResponse } from '../../../models/order.model';

/**
 * FIXED: Explicitly export types so components like 'Authorize'
 * can import them through this service
 */
export type { Order, OrderApiResponse, OrderStatistics, OrderFilters };

// ── Delivery Information types ──────────────────────────────────────────────
export interface DeliveryInformation {
  id?: number;
  order_id: number;
  delivery_mode_id: number;
  reference: string;
  vehicle_number: string;
  delivery_date: string;
}

export interface CreateDeliveryInformationPayload {
  order_id: number;
  delivery_mode_id: number;
  reference: string;
  vehicle_number: string;
  delivery_date: string;
}

export interface UpdateDeliveryInformationPayload {
  delivery_mode_id: number;
  reference: string;
  vehicle_number: string;
  delivery_date: string;
}

export interface DeliveryModeOption {
  id: number;
  delivery_mode_name: string;
  created_at?: string;
  updated_at?: string;
}
// ────────────────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/v1/orders';
  // endpoint for customer orders sits on a different path
  private readonly customerOrderUrl = '/api/v1/customer-orders';
  private readonly deliveryInfoUrl = '/api/v1/orders/delivery-information';
  private readonly deliveryModesUrl = '/api/v1/orders_info/delivery-modes';

  statistics = signal<OrderStatistics | null>(null);
  pagination = signal<OrderApiResponse['pagination'] | null>(null);
  activeOrderStatus = signal<string>('');
  processConfirmAction = signal<'hold' | 'cancel' | 'reject' | null>(null);

  /**
   * GET: Fetch orders with dynamic filters
   */
  getAll(filters?: OrderFilters): Observable<Order[]> {
    let params = new HttpParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }
    return this.http.get<OrderApiResponse>(`${this.baseUrl}/all`, { params }).pipe(
      tap(res => {
        if (res.statistics) this.statistics.set(res.statistics);
        if (res.pagination) this.pagination.set(res.pagination);
      }),
      map(res => res.data.map(order => this.mapOrderFields(order)))
    );
  }

  createCustomerOrder(payload: CustomerOrderPayload): Observable<any> {
    return this.http.post<any>(this.customerOrderUrl, payload);
  }

  /**
   * POST: mark a customer order as complete & get invoice data
   */
  completeCustomerOrder(id: number): Observable<any> {
    return this.http.post<any>(`${this.customerOrderUrl}/${id}/complete`, {});
  }

  /**
   * POST: create distributor order
   */
  createDistributorOrder(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}`, payload);
  }

  getById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${id}`).pipe(
      tap(res => {
        this.activeOrderStatus.set(res.data.order.order_status);
      })
    );
  }

  getCustomerOrderById(id: number): Observable<OrderDetailResponse> {
    return this.http.get<OrderDetailResponse>(`${this.customerOrderUrl}/${id}`);
  }

  getOrdersByCustomerId(
    customerId: number | string,
    perPage: number = 5,
    page: number = 1,
    filters?: { start_date?: string; end_date?: string }
  ): Observable<any> {
    let params = new HttpParams()
      .set('customer_id', customerId.toString())
      .set('per_page', perPage.toString())
      .set('page', page.toString());

    if (filters?.start_date) {
      params = params.set('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      params = params.set('end_date', filters.end_date);
    }

    return this.http.get<any>(`${this.customerOrderUrl}/get-orders`, { params });
  }

  authorizeOrder(id: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${id}/authorize`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res.data.order_status);
      })
    );
  }

  orderReady(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/order-ready`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res.data.order_status);
      })
    );
  }

  markAsInDelivery(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/in-delivery`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res.data.order_status);
      })
    );
  }

  deliveryComplete(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/delivery-complete`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res.data.order_status);
      })
    );
  }

  holdOrder(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/hold`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res?.data?.order_status || 'on_hold');
      })
    );
  }

  resumeOrder(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/resume`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res?.data?.order_status || 'in_progress');
      })
    );
  }

  cancelOrder(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/cancel`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res?.data?.order_status || 'canceled');
      })
    );
  }

  rejectOrder(id: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/reject`, {}).pipe(
      tap(res => {
        this.activeOrderStatus.set(res?.data?.order_status || 'rejected');
      })
    );
  }

  // ── Delivery Information ──────────────────────────────────────────────────

  /**
   * GET: Fetch delivery information for a specific order
   * GET /api/v1/orders/delivery-information/:id
   */
  getDeliveryInformation(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.deliveryInfoUrl}/${id}`, {
      context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
    });
  }

  getDeliveryModes(): Observable<any> {
    return this.http.get<any>(this.deliveryModesUrl);
  }

  /**
   * POST: Create delivery information for an order
   * POST /api/v1/orders/delivery-information
   */
  createDeliveryInformation(payload: CreateDeliveryInformationPayload): Observable<any> {
    return this.http.post<any>(this.deliveryInfoUrl, payload);
  }

  /**
   * PUT: Update existing delivery information
   * PUT /api/v1/orders/delivery-information/:id
   */
  updateDeliveryInformation(
    id: number | string,
    payload: UpdateDeliveryInformationPayload
  ): Observable<any> {
    return this.http.put<any>(`${this.deliveryInfoUrl}/${id}`, payload);
  }

  // ─────────────────────────────────────────────────────────────────────────

  private mapOrderFields(api: any): Order {
    return {
      id: api.order_id,
      orderNumber: api.order_number,
      invoiceNo: api.invoice_number,
      distributor: api.party_name ?? '',
      orderType: api.order_type ?? api.party_type,
      orderDate: api.order_date,
      products: api.number_of_products,
      orderStatus: api.order_status,
      orderTotal: api.order_total,
      daysFromInvoice: api.days_from_invoice,
      paymentStatus: api.payment_status,
    };
  }
}
